import React from "react";
import SectionHeader, { SectionWrapper, Reveal } from "./SectionHeader";
import { motion } from "framer-motion";

const BIG_STATS = [
  { value: "500+", label: "Macro Creators", color: "#00F0FF", tid: "network-macro" },
  { value: "50,000+", label: "Micro Creators", color: "#FF00FF", tid: "network-micro" },
  { value: "Pan-India", label: "Amplification", color: "#FFDE00", tid: "network-panindia" },
  { value: "Rapid", label: "Multi-Platform Scale", color: "#00F0FF", tid: "network-scale" },
];

const NODES = [
  { label: "50+ Niches", angle: -90, color: "#00F0FF" },
  { label: "Regional", angle: -45, color: "#FF00FF" },
  { label: "Tier 1 & 2", angle: 90, color: "#FFDE00" },
  { label: "Hyperlocal", angle: 225, color: "#00F0FF" },
];

const NetworkStrength = () => {
  return (
    <SectionWrapper id="network">
      <SectionHeader
        code="// CH.07 — INFRASTRUCTURE"
        title="Network.Grid"
        kicker="Not a list. A living grid of creators wired together for rapid, multi-platform deployment."
        tid="network"
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
        {/* Stats stack */}
        <div className="lg:col-span-5 grid grid-cols-2 gap-4">
          {BIG_STATS.map((s, i) => (
            <Reveal key={s.tid} delay={i * 0.05}>
              <div
                className="glass-card hud-corners p-6 md:p-7 h-full"
                data-testid={s.tid}
              >
                <span className="hud-tr" />
                <span className="hud-bl" />
                <div className="micro-label" style={{ color: s.color }}>
                  NODE · {String(i + 1).padStart(2, "0")}
                </div>
                <div
                  className="font-display font-black mt-3 interactive-text leading-none"
                  style={{
                    color: s.color,
                    textShadow: `0 0 18px ${s.color}55`,
                    fontSize: "clamp(1.1rem, 5vw, 2.6rem)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {s.value}
                </div>
                <div className="text-white/65 mt-2 text-sm md:text-base">{s.label}</div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Hub diagram */}
        <div className="lg:col-span-7">
          <Reveal>
            <div className="glass-card hud-corners p-8 md:p-12 relative overflow-hidden min-h-[460px] md:min-h-[520px]">
              <span className="hud-tr" />
              <span className="hud-bl" />
              <div className="flex items-center justify-between mb-6">
                <div className="micro-label text-cyan-400">CORE HUB · LIVE</div>
                <div className="micro-label text-white/40">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-dot mr-2 align-middle" />
                  STREAM · OK
                </div>
              </div>

              <div className="relative w-full h-[380px] md:h-[440px]">
                {/* SVG connections */}
                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid meet">
                  <defs>
                    <linearGradient id="line-c" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#00F0FF" stopOpacity="0.7" />
                      <stop offset="100%" stopColor="#00F0FF" stopOpacity="0.05" />
                    </linearGradient>
                    <linearGradient id="line-m" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#FF00FF" stopOpacity="0.7" />
                      <stop offset="100%" stopColor="#FF00FF" stopOpacity="0.05" />
                    </linearGradient>
                    <linearGradient id="line-y" x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor="#FFDE00" stopOpacity="0.7" />
                      <stop offset="100%" stopColor="#FFDE00" stopOpacity="0.05" />
                    </linearGradient>
                  </defs>
                  {NODES.map((n, i) => {
                    const rad = (n.angle * Math.PI) / 180;
                    const R = 160;
                    const x = 200 + Math.cos(rad) * R;
                    const y = 200 + Math.sin(rad) * R;
                    const GRADS = ["url(#line-c)", "url(#line-m)", "url(#line-y)"];
                    const grad = GRADS[i % 3];
                    return (
                      <line
                        key={n.label}
                        x1="200"
                        y1="200"
                        x2={x}
                        y2={y}
                        stroke={grad}
                        strokeWidth="1.2"
                        strokeDasharray="4 3"
                      />
                    );
                  })}
                  <circle cx="200" cy="200" r="140" stroke="rgba(0,240,255,0.12)" strokeWidth="1" fill="none" />
                  <circle cx="200" cy="200" r="90" stroke="rgba(0,240,255,0.08)" strokeWidth="1" fill="none" />
                </svg>

                {/* Center hub */}
                <motion.div
                  initial={{ scale: 0.7, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7 }}
                  className="absolute top-1/2 left-1/2 w-24 h-24 sm:w-28 sm:h-28 md:w-36 md:h-36 rounded-full flex flex-col items-center justify-center text-center"
                  style={{
                    x: "-50%",
                    y: "-50%",
                    background: "radial-gradient(circle, rgba(0,240,255,0.2), rgba(0,0,0,0.6))",
                    border: "1px solid rgba(0,240,255,0.6)",
                    boxShadow: "0 0 50px rgba(0,240,255,0.35), inset 0 0 30px rgba(0,240,255,0.15)",
                  }}
                  data-testid="network-hub"
                >
                  <span className="micro-label text-cyan-300 text-[9px] sm:text-[11px]">CORE</span>
                  <span className="font-display font-black text-base sm:text-lg md:text-xl mt-1 glow-cyan leading-none">U&apos;TH</span>
                  <span className="font-display font-black text-base sm:text-lg md:text-xl leading-none">HUB</span>
                </motion.div>

                {/* Node labels — wrapper handles positioning, motion handles animation */}
                {NODES.map((n, i) => {
                  const rad = (n.angle * Math.PI) / 180;
                  const R = 38;
                  const leftPct = 50 + Math.cos(rad) * R;
                  const topPct = 50 + Math.sin(rad) * R;
                  return (
                    <div
                      key={n.label}
                      className="absolute"
                      style={{
                        left: `${leftPct}%`,
                        top: `${topPct}%`,
                        transform: "translate(-50%, -50%)",
                      }}
                      data-testid={`network-node-${i}`}
                    >
                      <motion.div
                        initial={{ opacity: 0, scale: 0.6 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 + i * 0.1, duration: 0.5 }}
                        className="px-2 sm:px-3 md:px-4 py-1.5 md:py-2 border font-mono-uth uppercase text-[9px] sm:text-[10px] md:text-xs tracking-widest whitespace-nowrap"
                        style={{
                          borderColor: `${n.color}66`,
                          color: n.color,
                          background: "rgba(10,12,20,0.85)",
                          backdropFilter: "blur(10px)",
                          boxShadow: `0 0 18px ${n.color}33`,
                        }}
                      >
                        {n.label}
                      </motion.div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </SectionWrapper>
  );
};

export default NetworkStrength;
